import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { selectIsUserLogged } from '@selectors/auth';
import { selectShopEventStatus } from '@selectors/shopEvent';

import { getRouteUrl } from '@utils/urls';

import ShopEventProductList from '@common/components/ShopEventProductList';
import HeroSection from '@common/components/HeroSection';
import UserCounter from '@common/components/UserCounter';
import CMSComponentsRenderer from '@common/components/CMSComponents/CMSComponentsRenderer';
import PaymentBenefitBlock from '@common/components/PaymentBenefitBlock';
import AlertBox from '@common/components/AlertBox';
import useTimeoutToggle from '@common/hooks/useTimeoutToggle';
import { HOME, SHOP_EVENT, REGISTER } from '@common/routes';
import Layout from '@common/containers/Layout';
import RouteLayout from '@common/containers/RouteLayout';
import { SHOP_EVENT_STATUS } from '@common/constants/shopEvent';
import { VARIANT_QUATERNARY } from '@common/constants/alertBox';
import ContentWrapper from '@common/components/ContentWrapper';
import { videoShape } from '@common/types/cms';

import Countdown from './Countdown';
import { AlertWrapper } from './ShopEvent.styled';

const ShopEvent = ({
  pageData: {
    meta: { metaTitle, metaDescription, metaImage },
    titleCurrent,
    image,
    leadCurrent,
    body,
    video,
    videoDesktop,
  },
}) => {
  const { t } = useTranslation();
  const { locale, push } = useRouter();
  const pageUrl = getRouteUrl(locale, SHOP_EVENT);
  const isUserLogged = useSelector(selectIsUserLogged);
  const { shopEventStatus, flashSaleMode } = useSelector(selectShopEventStatus);
  const [isCounterVisible, hideCounter] = useTimeoutToggle();
  const isInactive = shopEventStatus === SHOP_EVENT_STATUS.INACTIVE;

  return (
    <Layout
      title={metaTitle}
      description={metaDescription}
      ogImage={metaImage?.url}
      canonicalPageUrl={pageUrl}
      footerNoSpacingTop
    >
      <RouteLayout
        backRoute={HOME}
        backRouteAs={getRouteUrl(locale, HOME)}
        columns={12}
        stickyTitle={titleCurrent}
        isHero={!!image}
      >
        <HeroSection
          title={titleCurrent}
          lead={leadCurrent}
          image={{
            src: image?.url,
            alt: image?.altText,
          }}
          video={video}
          videoDesktop={videoDesktop}
          noSpacing={!flashSaleMode}
        />

        {!isInactive && <Countdown />}

        {shopEventStatus !== SHOP_EVENT_STATUS.OPEN && (
          <ContentWrapper data-testid="contentWrapper">
            {isInactive && !isUserLogged && (
              <AlertWrapper>
                <AlertBox
                  description={t(
                    'shop.noevent.register.text',
                    'Als VIP erhälst Du, neben zahrleichen anderen Vorteilen, rechtzeitig vor den Chicorée Moments eine Benachrichtigung, die Dich an den Event erinnert.',
                  )}
                  title={t(
                    'shop.noevent.register.title',
                    'Erstelle jetzt Dein VIP-Konto',
                  )}
                  buttonText={t(
                    'shop.list.register.button.label',
                    'Zur Registrierung',
                  )}
                  onButtonClick={() =>
                    push(REGISTER, getRouteUrl(locale, REGISTER))
                  }
                  variant={VARIANT_QUATERNARY}
                />
              </AlertWrapper>
            )}
            <CMSComponentsRenderer body={body} />
          </ContentWrapper>
        )}

        {shopEventStatus === SHOP_EVENT_STATUS.OPEN && (
          <ShopEventProductList
            stickyContainerBottomSpacing={isCounterVisible}
            divider={<Countdown />}
            isFlashSale={flashSaleMode}
          />
        )}

        <PaymentBenefitBlock spacingBottom={['section', 0]} />

        {shopEventStatus === SHOP_EVENT_STATUS.OPEN && flashSaleMode && (
          <UserCounter
            isCounterVisible={isCounterVisible}
            hideCounter={hideCounter}
          />
        )}
      </RouteLayout>
    </Layout>
  );
};

ShopEvent.propTypes = {
  pageData: PropTypes.shape({
    body: PropTypes.arrayOf(PropTypes.shape({})),
    image: PropTypes.shape({
      altText: PropTypes.string,
      url: PropTypes.string,
    }),
    video: videoShape,
    videoDesktop: videoShape,
    leadCurrent: PropTypes.string,
    meta: PropTypes.shape({
      metaDescription: PropTypes.string,
      metaTitle: PropTypes.string.isRequired,
      metaImage: PropTypes.shape({
        url: PropTypes.string,
      }),
    }).isRequired,
    titleCurrent: PropTypes.string.isRequired,
  }).isRequired,
};

export default ShopEvent;
